import { shuffle } from '../utils'

export type Difficulty = 'easy' | 'medium' | 'hard'
export type Direction  = 'H' | 'V'

export const DIFFICULTY_WORD_COUNT: Record<Difficulty, number> = {
  easy:   3,
  medium: 5,
  hard:   7,
}

// Word length range per difficulty — minimum 3 for all, maximum scales with difficulty
const WORD_LEN: Record<Difficulty, [number, number]> = {
  easy:   [3, 5],
  medium: [3, 6],
  hard:   [3, 7],
}

// Max distinct letters in the alphabet per difficulty
const MAX_ALPHABET: Record<Difficulty, number> = {
  easy:   4,
  medium: 5,
  hard:   6,
}

// Common letters used for alphabet padding
const COMMON = 'AEIORSTNLM'

export interface PlacedWord {
  id:   number
  word: string    // uppercase, A-Z only
  row:  number
  col:  number
  dir:  Direction
}

export interface CrosswordPuzzle {
  placedWords: PlacedWord[]
  letters:     string[]   // circle letters (derived from words), shuffled
  rows:        number
  cols:        number
  rowOffset:   number     // add to placedWord.row to get grid row index
  colOffset:   number
}

// Derive the minimal letter multiset that lets every word be spelled.
// For each letter, take the MAX count required by any single word.
function deriveLetterPool(words: string[]): string[] {
  const maxCounts = new Map<string, number>()
  for (const word of words) {
    const wc = new Map<string, number>()
    for (const c of word) wc.set(c, (wc.get(c) ?? 0) + 1)
    for (const [c, n] of wc) maxCounts.set(c, Math.max(maxCounts.get(c) ?? 0, n))
  }
  return [...maxCounts.entries()].flatMap(([c, n]) => Array<string>(n).fill(c))
}

// ── Grid helpers ──────────────────────────────────────────────────────

type OccupiedMap = Map<string, { letter: string; wordId: number }>

function gkey(r: number, c: number): string { return `${r},${c}` }

function buildOccupied(placed: PlacedWord[]): OccupiedMap {
  const map: OccupiedMap = new Map()
  for (const pw of placed) {
    const dr = pw.dir === 'V' ? 1 : 0
    const dc = pw.dir === 'H' ? 1 : 0
    for (let i = 0; i < pw.word.length; i++) {
      map.set(gkey(pw.row + i * dr, pw.col + i * dc), {
        letter: pw.word[i],
        wordId: pw.id,
      })
    }
  }
  return map
}

function canPlace(
  word:     string,
  row:      number,
  col:      number,
  dir:      Direction,
  occupied: OccupiedMap,
  requireCrossing: boolean,
): boolean {
  const dr = dir === 'V' ? 1 : 0
  const dc = dir === 'H' ? 1 : 0

  // Buffer before and after the word
  if (occupied.has(gkey(row - dr, col - dc))) return false
  if (occupied.has(gkey(row + dr * word.length, col + dc * word.length))) return false

  let hasCrossing = false

  for (let i = 0; i < word.length; i++) {
    const r = row + i * dr
    const c = col + i * dc
    const k = gkey(r, c)
    const existing = occupied.get(k)

    if (existing) {
      if (existing.letter !== word[i]) return false
      hasCrossing = true
    } else {
      // Perpendicular neighbors must be empty
      if (occupied.has(gkey(r + dc, c + dr))) return false
      if (occupied.has(gkey(r - dc, c - dr))) return false
    }
  }

  return requireCrossing ? hasCrossing : true
}

function findPlacements(
  word:     string,
  occupied: OccupiedMap,
  requireCrossing: boolean,
): Array<{ row: number; col: number; dir: Direction }> {
  if (!requireCrossing) return [{ row: 0, col: 0, dir: 'H' }]

  const placements: Array<{ row: number; col: number; dir: Direction }> = []

  for (const [k, cell] of occupied) {
    const [r, c] = k.split(',').map(Number)
    for (let i = 0; i < word.length; i++) {
      if (word[i] !== cell.letter) continue
      for (const dir of ['H', 'V'] as Direction[]) {
        const dr = dir === 'V' ? 1 : 0
        const dc = dir === 'H' ? 1 : 0
        const startRow = r - i * dr
        const startCol = c - i * dc
        if (canPlace(word, startRow, startCol, dir, occupied, true)) {
          placements.push({ row: startRow, col: startCol, dir })
        }
      }
    }
  }

  return placements
}

function buildLayout(candidates: string[], targetCount: number): PlacedWord[] | null {
  const pool = shuffle([...candidates]).slice(0, 30)

  function backtrack(placed: PlacedWord[], remaining: string[]): PlacedWord[] | null {
    if (placed.length === targetCount) return placed
    if (remaining.length === 0) return null

    const occupied = buildOccupied(placed)

    for (let i = 0; i < remaining.length; i++) {
      const word = remaining[i]
      const rest = [...remaining.slice(0, i), ...remaining.slice(i + 1)]
      const allPlacements = findPlacements(word, occupied, placed.length > 0)
      const placements = shuffle(allPlacements).slice(0, 6)

      for (const pos of placements) {
        const pw: PlacedWord = { id: placed.length, word, ...pos }
        const result = backtrack([...placed, pw], rest)
        if (result) return result
      }
    }

    return null
  }

  return backtrack([], pool)
}

// ── Main: build a puzzle ─────────────────────────────────────────────
//
// Strategy:
// 1. Pick a random anchor word → its unique letters become the alphabet seed.
//    Pad to MAX_ALPHABET distinct letters with common letters.
// 2. Find all clean words whose every letter is in that alphabet
//    (alphabet is a membership check — letters can repeat within a word).
//    This gives a large candidate pool compared to a multiset constraint.
// 3. Build crossword layout from candidates.
// 4. Derive the circle from placed words: max count of each letter
//    needed by any single word. Guarantees every word is spellable.

export function buildPuzzle(
  wordPool: string[],
  difficulty: Difficulty,
): CrosswordPuzzle | null {
  const targetWordCount = DIFFICULTY_WORD_COUNT[difficulty]
  const [minLen, maxLen] = WORD_LEN[difficulty]

  const clean = [...new Set(wordPool)].filter(
    w => w.length >= minLen && w.length <= maxLen && /^[A-Z]+$/.test(w),
  )

  if (clean.length < targetWordCount * 3) return null

  for (let attempt = 0; attempt < 80; attempt++) {
    const maxAlpha = MAX_ALPHABET[difficulty]

    // Pick anchor word with ≤ maxAlpha unique letters
    const anchor = clean[Math.floor(Math.random() * clean.length)]
    const alphabet = new Set(anchor.split(''))
    if (alphabet.size > maxAlpha) continue

    // Pad alphabet to maxAlpha with common letters not already present
    for (const c of shuffle([...COMMON])) {
      if (alphabet.size >= maxAlpha) break
      alphabet.add(c)
    }

    // Candidates: any clean word whose every letter is in the alphabet
    const candidates = clean.filter(w => w.split('').every(c => alphabet.has(c)))
    if (candidates.length < targetWordCount * 2) continue

    // Build crossword layout
    const placed = buildLayout(candidates, targetWordCount)
    if (!placed) continue

    // Derive circle: max count of each letter needed by any single placed word
    const letters = shuffle(deriveLetterPool(placed.map(pw => pw.word)))

    // Compute grid bounds
    let minRow = 0, maxRow = 0, minCol = 0, maxCol = 0
    for (const pw of placed) {
      const dr = pw.dir === 'V' ? 1 : 0
      const dc = pw.dir === 'H' ? 1 : 0
      minRow = Math.min(minRow, pw.row)
      maxRow = Math.max(maxRow, pw.row + dr * (pw.word.length - 1))
      minCol = Math.min(minCol, pw.col)
      maxCol = Math.max(maxCol, pw.col + dc * (pw.word.length - 1))
    }

    return {
      placedWords: placed,
      letters,
      rows:        maxRow - minRow + 1,
      cols:        maxCol - minCol + 1,
      rowOffset:   -minRow,
      colOffset:   -minCol,
    }
  }

  return null
}
